import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import OnboardingConstants from 'constants/OnboardingConstants';
import {TUTORIAL_ROUTES} from 'constants/TutorialConstants';
// @ts-expect-error
import * as tutorial from 'help/eln/Tutorial';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export const ENTRY_CHECKLIST_KEY = 'notebook';
export const ENTRY_CHECKLIST_TITLE = 'Explore Notebook to Get More Space';
export const SEQUENCE_CHECKLIST_KEY = 'molecular_biology';
export const SEQUENCE_CHECKLIST_TITLE = 'Explore Molecular Biology to Earn Space';
export const GETTING_STARTED_CHECKLIST_KEY = 'sidepanel';
export const GETTING_STARTED_CHECKLIST_TITLE = 'Getting Started';

export const ANALYTICS_LABELS = {
  [OnboardingConstants.MODULES.GETTING_STARTED]: {
    key: GETTING_STARTED_CHECKLIST_KEY,
    title: GETTING_STARTED_CHECKLIST_TITLE,
  },
  [OnboardingConstants.MODULES.ENTRY]: {
    key: ENTRY_CHECKLIST_KEY,
    title: ENTRY_CHECKLIST_TITLE,
  },
  [OnboardingConstants.MODULES.SEQUENCE]: {
    key: SEQUENCE_CHECKLIST_KEY,
    title: SEQUENCE_CHECKLIST_TITLE,
  },
};

export const LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS = {
  [OnboardingConstants.MODULES.ENTRY]: 'benchling.onboarding.hide-entry-nue-module',
  [OnboardingConstants.MODULES.SEQUENCE]: 'benchling.onboarding.hide-sequence-nue-module',
};
export interface NUEModuleEnhancedProps {
  visible: boolean;
  tasks: Array<TaskStatus> | null;
  completed: boolean;
  collapsed?: boolean;
  openIndex: number | null;
  isSampleEntry?: boolean;
}

export interface TaskStatus {
  key: string;
  completed: boolean;
}

export interface EntryTaskInfo {
  title: string;
  description: (isSampleEntry: boolean) => string | JSX.Element;
  steps?: Array<string>;
}

export interface SequenceTaskInfo {
  title: string;
  steps: Array<string>;
  tutorialLink?: string;
}

export interface GettingStartedTaskInfo {
  title: string;
  onClick: () => void;
  resultForTracking: string;
}

export interface OnboardingTask {
  key: string;
  completed: boolean;
  title: string;
  countTowardCompletion: boolean;
  description?: string | JSX.Element;
  tutorialLink?: string;
  steps?: Array<string>;
  onClick?: () => void;
  resultForTracking?: string;
}

export const ENTRY_TASKS: {[key: string]: EntryTaskInfo} = _.object([
  [
    OnboardingConstants.CREATE_ENTRY,
    {
      title: 'Create a new entry',
      description: () => (
        <div>
          {'Click '}
          <BenchlingIcon icon="compose" />
          {' on the left then "Blank entry" to create your first entry from scratch.'}
        </div>
      ),
    },
  ],
  [
    OnboardingConstants.CREATE_EDIT_TABLE,
    {
      title: 'Create and edit a table',
      description: () => 'Insert a table and try using a formula.',
    },
  ],
  [
    OnboardingConstants.INSERT_DAY,
    {
      title: `Insert a new ${BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS ? 'section' : 'day'}`,
      description: () =>
        `Find "${
          BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS ? 'Section' : 'New Day'
        }" in the Insert menu in the top toolbar.`,
    },
  ],
  [
    OnboardingConstants.LINK_SEQUENCE,
    {
      title: 'Link a sequence',
      description: () => 'Type "@pBR322_EGFR" to mention a sequence in your entry.',
    },
  ],
  [
    OnboardingConstants.ATTACH_PROTOCOL,
    {
      title: 'Attach a protocol',
      description: () => 'Click "Add Protocol" and choose a protocol to attach.',
    },
  ],
]);

export const SEQUENCE_TASKS: {[key: string]: SequenceTaskInfo} = _.object([
  [
    OnboardingConstants.SPLIT_WORKSPACE,
    {
      title: 'Split your workspace',
      steps: ['Click "Split Workspace" below this to see a plasmid map alongside the sequence map.'],
    },
  ],
  [
    OnboardingConstants.AUTO_ANNOTATE,
    {
      title: 'Add annotations',
      steps: [
        'Click on the Annotations button - the first gray button on the right side panel.',
        'Click "Auto-annotate" to search for a pre-populated library of annotation features.',
        'Search for the “Default Features” library.',
        'Add the two matching features to the sequence as annotations by clicking "Add Annotations."',
      ],
    },
  ],
  [
    OnboardingConstants.RUN_DIGEST,
    {
      title: 'Run a digest',
      steps: [
        'Click on the Digests button - the gray scissors on the right side panel.',
        'Under "Find Enzyme," click to select two restriction enzymes in the list.',
        'Click "Run Digest."',
        'To see virtual results, click the "Virtual Digest" tab toward the top of your screen.',
      ],
      tutorialLink: TUTORIAL_ROUTES.MOLBIO_SIMULATE_DIGEST,
    },
  ],
  [
    OnboardingConstants.VIEW_ALIGNMENT,
    {
      title: 'View an alignment',
      steps: [
        'Click on the Alignments button on the right side panel.',
        'Find "Saved Alignments" - a history of all other alignments performed against this sequence.',
        'Click on the saved alignment to open and view the results of example sequencing preps.',
      ],
    },
  ],
  [
    OnboardingConstants.IMPORT_SEQUENCE,
    {
      title: 'Import a new sequence',
      steps: [
        `Click "+" on the left then "DNA / RNA Sequence" and "New DNA / RNA Sequence."`,
        'Find the "Search External Databases" tab to search for a sequence from a database.',
        'Import the sequence you searched for!',
      ],
    },
  ],
]);

// We use a Map here to preserve insertion order
export const GETTING_STARTED_TASKS: Map<string, GettingStartedTaskInfo> = new Map([
  [
    OnboardingConstants.VERIFY_EMAIL,
    {
      title: 'Verify your email address',
      onClick: () => {
        window.open(`/${EntityStore.currentUser().url_identifier}/settings`, '_blank', 'noopener,noreferrer');
      },
      resultForTracking: 'page_profile_settings_opened',
    },
  ],
  [
    OnboardingConstants.CREATE_OR_JOIN_ORG,
    {
      title: 'Create or join an organization',
      onClick: () => {
        HomeDashboardActions.openSearchOrgModal();
      },
      resultForTracking: 'modal_join_organization_opened',
    },
  ],
  [
    OnboardingConstants.CREATE_FIRST_FILE,
    {
      title: 'Create your first item',
      onClick: () => {
        SidepanelActions.openCreateMenu();
      },
      resultForTracking: 'menu_global_create_opened',
    },
  ],
  [
    OnboardingConstants.MAIN_TOUR,
    {
      title: 'Take a tour of Benchling',
      onClick: () => {
        tutorial.runTutorial();
      },
      resultForTracking: 'tour_navigation_opened',
    },
  ],
  [
    OnboardingConstants.SET_RECOVERY_EMAIL,
    {
      title: 'Set a recovery email address',
      onClick: () => {
        window.open(`/${EntityStore.currentUser().url_identifier}/settings`, '_blank', 'noopener,noreferrer');
      },
      resultForTracking: 'page_profile_settings_opened',
    },
  ],
]);
